import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Spinner, Table, Form, Button, OverlayTrigger, Pagination, Tooltip as BootstrapTooltip } from 'react-bootstrap';
import { FiInfo } from 'react-icons/fi';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { fileAPI } from '../utils/api';
import './DashboardPage.css';

const formatCurrency = (value) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return 'INR 0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(number);
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

const buildAmountData = (rows, fieldName, amountFieldName) => {
  const totals = {};

  rows.forEach((row) => {
    const key = String(row?.[fieldName] || 'N/A').trim() || 'N/A';
    const amount = Number(row?.[amountFieldName] || 0);
    if (!Number.isFinite(amount)) return;
    totals[key] = (totals[key] || 0) + amount;
  });

  return Object.entries(totals)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value);
};

const normalizeBgCategory = (bgTypeValue) => {
  const value = String(bgTypeValue || '').trim().toLowerCase();
  if (!value) return 'Other';
  if (value.includes('cash') && value.includes('emd')) return 'Cash EMD';
  if (value.includes('bg')) return 'BG';
  return 'Other';
};

const getTrendDateValue = (row) => {
  return row?.issueDate || row?.targetTimeline || row?.validUpto || row?.claimUpto || null;
};

const buildFinanceSummaryFromRows = (rows) => {
  const trendBuckets = {};
  let totalAmount = 0;
  let bgAmount = 0;

  rows.forEach((row) => {
    const amount = Number(row?.amountInr || 0);
    if (!Number.isFinite(amount)) return;

    totalAmount += amount;

    const category = normalizeBgCategory(row?.bgType);
    if (category === 'BG') {
      bgAmount += amount;
    }

    const dateValue = getTrendDateValue(row);
    const date = dateValue ? new Date(dateValue) : null;
    if (!date || Number.isNaN(date.getTime())) return;
    const bucketKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    trendBuckets[bucketKey] = (trendBuckets[bucketKey] || 0) + amount;
  });

  const trendData = Object.entries(trendBuckets)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([bucketKey, value]) => {
      const [year, month] = bucketKey.split('-');
      const labelDate = new Date(Number(year), Number(month) - 1, 1);
      return {
        name: labelDate.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }),
        value
      };
    });

  return {
    stats: {
      totalAmount,
      bgAmount,
      totalEntries: rows.length,
      uniqueEntities: 0,
      totalFinanceFiles: 0
    },
    trendData
  };
};

const getDaysUntilDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return Math.round((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

const getValidityMeta = (row) => {
  const daysRemaining = getDaysUntilDate(row?.validUpto);
  if (daysRemaining === null) {
    return { label: 'Unknown', className: 'dashboard-row-unknown', sortValue: null };
  }
  if (daysRemaining < 0) {
    return { label: `Expired ${Math.abs(daysRemaining)}d ago`, className: 'dashboard-row-expired', sortValue: daysRemaining };
  }
  if (daysRemaining <= 30) {
    return { label: `${daysRemaining}d left`, className: 'dashboard-row-critical', sortValue: daysRemaining };
  }
  if (daysRemaining <= 90) {
    return { label: `${daysRemaining}d left`, className: 'dashboard-row-warning', sortValue: daysRemaining };
  }
  return { label: `${daysRemaining}d left`, className: 'dashboard-row-safe', sortValue: daysRemaining };
};

const getActiveValidityCellClass = (daysRemaining) => {
  if (daysRemaining === null) return 'dashboard-validity-neutral';
  if (daysRemaining <= 7) return 'dashboard-validity-red-900';
  if (daysRemaining <= 30) return 'dashboard-validity-red-700';
  if (daysRemaining <= 90) return 'dashboard-validity-red-500';
  return 'dashboard-validity-red-300';
};

const getRiskClassification = (daysRemaining) => {
  if (daysRemaining === null) return { label: 'Unknown', className: 'risk-neutral' };
  if (daysRemaining < 30) return { label: 'High Risk (<30d)', className: 'risk-high' };
  if (daysRemaining < 90) return { label: 'Medium Risk (<90d)', className: 'risk-medium' };
  return { label: 'Low Risk (>90d)', className: 'risk-low' };
};

const buildExpiryTimelineData = (rows) => {
  const buckets = {};

  rows.forEach((row) => {
    const validDate = row?.validUpto ? new Date(row.validUpto) : null;
    if (!validDate || Number.isNaN(validDate.getTime())) return;
    const key = `${validDate.getFullYear()}-${String(validDate.getMonth() + 1).padStart(2, '0')}`;
    buckets[key] = (buckets[key] || 0) + 1;
  });

  return Object.entries(buckets)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => {
      const [year, month] = key.split('-');
      const labelDate = new Date(Number(year), Number(month) - 1, 1);
      return {
        name: labelDate.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' }),
        value
      };
    });
};

const getHighestExposureProject = (rows) => {
  const exposureByProject = {};

  rows.forEach((row) => {
    const projectName = String(row?.projectName || 'N/A').trim() || 'N/A';
    const amount = Number(row?.amountInr || 0);
    if (!Number.isFinite(amount)) return;
    exposureByProject[projectName] = (exposureByProject[projectName] || 0) + amount;
  });

  const ranked = Object.entries(exposureByProject)
    .map(([project, amount]) => ({ project, amount }))
    .sort((a, b) => b.amount - a.amount);

  return ranked[0] || { project: '-', amount: 0 };
};

const INITIAL_ACTIONAL_FORM = {
  finalProject: '',
  validUpto: '',
  owner: '',
  purpose: '',
  bu: '',
  projectName: '',
  beneficiaryName: '',
  referenceNo: '',
  bgType: '',
  issueDate: '',
  bankName: '',
  bgNo: '',
  amountInr: '',
  claimUpto: '',
  targetTimeline: '',
  remarks: ''
};

const FinanceDashboardPage = ({ departmentId = null, departmentName = '' }) => {
  const [actionalBgRows, setActionalBgRows] = useState([]);
  const [actionalBgError, setActionalBgError] = useState('');
  const [canSeeFinanceDashboard, setCanSeeFinanceDashboard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [showAddForm, setShowAddForm] = useState(false);
  const [addFormSubmitting, setAddFormSubmitting] = useState(false);
  const [addFormError, setAddFormError] = useState('');
  const [addFormSuccess, setAddFormSuccess] = useState('');
  const [addFormData, setAddFormData] = useState(INITIAL_ACTIONAL_FORM);
  const [activePage, setActivePage] = useState(1);
  const [activePageSize, setActivePageSize] = useState(10);
  const [expiredPage, setExpiredPage] = useState(1);
  const [expiredPageSize, setExpiredPageSize] = useState(10);


  const COLORS = ['#667eea', '#f093fb', '#4facfe', '#764ba2'];

  const ownerOptions = useMemo(() => {
    return [...new Set(actionalBgRows.map((row) => String(row?.owner || '').trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
  }, [actionalBgRows]);

  const projectOptions = useMemo(() => {
    return [...new Set(actionalBgRows.map((row) => String(row?.projectName || '').trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
  }, [actionalBgRows]);

  const filteredRows = useMemo(() => {
    return actionalBgRows.filter((row) => {
      const owner = String(row?.owner || '').trim();
      const projectName = String(row?.projectName || '').trim();

      const ownerMatch = ownerFilter === 'all' || owner === ownerFilter;
      const projectMatch = projectFilter === 'all' || projectName === projectFilter;
      return ownerMatch && projectMatch;
    });
  }, [actionalBgRows, ownerFilter, projectFilter]);

  const sortedRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const aMeta = getValidityMeta(a);
      const bMeta = getValidityMeta(b);
      if (aMeta.sortValue === null && bMeta.sortValue === null) return 0;
      if (aMeta.sortValue === null) return -1;
      if (bMeta.sortValue === null) return 1;
      return aMeta.sortValue - bMeta.sortValue;
    });
  }, [filteredRows]);

  const activeRows = useMemo(() => {
    return sortedRows.filter((row) => {
      const meta = getValidityMeta(row);
      return meta.sortValue === null || meta.sortValue >= 0;
    });
  }, [sortedRows]);

  const expiredRows = useMemo(() => {
    return sortedRows
      .filter((row) => {
        const meta = getValidityMeta(row);
        return meta.sortValue !== null && meta.sortValue < 0;
      })
      .sort((a, b) => {
        const aMeta = getValidityMeta(a);
        const bMeta = getValidityMeta(b);
        return bMeta.sortValue - aMeta.sortValue;
      });
  }, [sortedRows]);

  const activeTotalPages = useMemo(
    () => Math.max(1, Math.ceil(activeRows.length / activePageSize)),
    [activeRows.length, activePageSize]
  );
  const expiredTotalPages = useMemo(
    () => Math.max(1, Math.ceil(expiredRows.length / expiredPageSize)),
    [expiredRows.length, expiredPageSize]
  );

  const activePageSafe = Math.min(activePage, activeTotalPages);
  const expiredPageSafe = Math.min(expiredPage, expiredTotalPages);

  const paginatedActiveRows = useMemo(() => {
    const start = (activePageSafe - 1) * activePageSize;
    return activeRows.slice(start, start + activePageSize);
  }, [activeRows, activePageSafe, activePageSize]);

  const paginatedExpiredRows = useMemo(() => {
    const start = (expiredPageSafe - 1) * expiredPageSize;
    return expiredRows.slice(start, start + expiredPageSize);
  }, [expiredRows, expiredPageSafe, expiredPageSize]);

  const financeSummary = useMemo(() => buildFinanceSummaryFromRows(filteredRows), [filteredRows]);
  const kpiSummary = useMemo(() => {
    let expiringIn30 = 0;
    let expiringIn90 = 0;
    let overdueClaim = 0;

    filteredRows.forEach((row) => {
      const validDays = getDaysUntilDate(row?.validUpto);
      if (validDays !== null && validDays >= 0 && validDays <= 30) expiringIn30 += 1;
      if (validDays !== null && validDays >= 0 && validDays <= 90) expiringIn90 += 1;

      const claimDays = getDaysUntilDate(row?.claimUpto);
      if (claimDays !== null && claimDays < 0) overdueClaim += 1;
    });

    const highestExposure = getHighestExposureProject(filteredRows);
    return {
      expiringIn30,
      expiringIn90,
      overdueClaim,
      highestExposure
    };
  }, [filteredRows]);

  const projectExposureBarData = useMemo(
    () => buildAmountData(filteredRows, 'projectName', 'amountInr'),
    [filteredRows]
  );
  const bankExposureDonutData = useMemo(
    () => buildAmountData(filteredRows, 'bankName', 'amountInr'),
    [filteredRows]
  );
  const expiryTimelineData = useMemo(
    () => buildExpiryTimelineData(activeRows),
    [activeRows]
  );
  const ownerResponsibilityBarData = useMemo(
    () => buildAmountData(filteredRows, 'owner', 'amountInr'),
    [filteredRows]
  );

  useEffect(() => {
    setActivePage(1);
    setExpiredPage(1);
  }, [ownerFilter, projectFilter, activePageSize, expiredPageSize]);

  useEffect(() => {
    if (activePage > activeTotalPages) setActivePage(activeTotalPages);
  }, [activePage, activeTotalPages]);

  useEffect(() => {
    if (expiredPage > expiredTotalPages) setExpiredPage(expiredTotalPages);
  }, [expiredPage, expiredTotalPages]);

  // `fetchFinanceMetrics` is declared below; it's safe to ignore exhaustive-deps here
  const fetchFinanceMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      if (!departmentId) {
        setError('Department is required to load finance dashboard data.');
        setCanSeeFinanceDashboard(true);
        return;
      }

      try {
        const actionalBgResponse = await fileAPI.getActionalBgDetails({ departmentId });
        const bgRows = Array.isArray(actionalBgResponse?.data?.rows)
          ? actionalBgResponse.data.rows
          : [];

        setActionalBgRows(bgRows);
        setActionalBgError('');
      } catch (err) {
        setActionalBgRows([]);
        setActionalBgError(err?.message ? `Failed to load Actional BG details table. ${err.message}` : 'Failed to load Actional BG details table.');
      }

      setCanSeeFinanceDashboard(true);
    } catch (err) {
      const message = err?.message || '';
      if (message.includes('403')) {
        setCanSeeFinanceDashboard(false);
      } else {
        setError('Failed to load finance dashboard data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [departmentId]);

  useEffect(() => {
    fetchFinanceMetrics();
  }, [fetchFinanceMetrics]);

  const handleAddFormChange = (field, value) => {
    setAddFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateActionalBgDetail = async (event) => {
    event.preventDefault();
    setAddFormError('');
    setAddFormSuccess('');

    if (!departmentId) {
      setAddFormError('Department context is required.');
      return;
    }

    const amountValue = String(addFormData.amountInr || '').trim();
    if (amountValue && Number.isNaN(Number(amountValue.replace(/,/g, '')))) {
      setAddFormError('Amount (INR) must be a valid number.');
      return;
    }

    try {
      setAddFormSubmitting(true);

      await fileAPI.createActionalBgDetail({
        departmentId,
        ...addFormData
      });

      setAddFormSuccess('Actional BG entry added successfully.');
      setAddFormData(INITIAL_ACTIONAL_FORM);
      await fetchFinanceMetrics();
    } catch (err) {
      setAddFormError(err?.message || 'Failed to add Actional BG entry.');
    } finally {
      setAddFormSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <Container fluid className="dashboard-page py-4">
      {error && <Alert variant="danger">{error}</Alert>}

      {!canSeeFinanceDashboard ? (
        <Alert variant="warning">Finance dashboard is available only for Finance users and EXCO.</Alert>
      ) : (
        <>
          <Row className="g-3 mb-3 row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-6">
            <Col>
              <StatCard
                title="Total BG Amount"
                value={formatCurrency(financeSummary.stats.bgAmount)}
                color="success"
                tooltip="Total BG exposure amount from current filtered Actional BG rows."
              />
            </Col>
            <Col>
              <StatCard
                title="Total BG Records"
                value={financeSummary.stats.totalEntries}
                color="warning"
                tooltip="Total number of Actional BG records after current Owner/Project filters."
              />
            </Col>
            <Col>
              <StatCard
                title="BG Expiring in Next 30 Days"
                value={kpiSummary.expiringIn30}
                color="danger"
                tooltip="Count of BG entries where Valid Upto is within the next 30 days."
              />
            </Col>
            <Col>
              <StatCard
                title="BG Expiring in Next 90 Days"
                value={kpiSummary.expiringIn90}
                color="warning"
                tooltip="Count of BG entries where Valid Upto is within the next 90 days."
              />
            </Col>
            <Col>
              <StatCard
                title="Overdue Claim Upto"
                value={kpiSummary.overdueClaim}
                color="danger"
                tooltip="Count of entries where Claim Upto date is already in the past."
              />
            </Col>
            <Col>
              <StatCard
                title={`Highest Exposure (${kpiSummary.highestExposure.project})`}
                value={formatCurrency(kpiSummary.highestExposure.amount)}
                color="secondary"
                tooltip="Project with highest cumulative exposure amount in current filtered data."
              />
            </Col>
          </Row>

          <Card className="shadow-sm mb-4">
            <Card.Body>
              <Row className="g-3 align-items-end">
                <Col md={5}>
                  <Form.Label className="mb-1">Owner</Form.Label>
                  <Form.Select value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}>
                    <option value="all">All Owners</option>
                    {ownerOptions.map((owner) => (
                      <option key={owner} value={owner}>{owner}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={5}>
                  <Form.Label className="mb-1">Project Name</Form.Label>
                  <Form.Select value={projectFilter} onChange={(e) => setProjectFilter(e.target.value)}>
                    <option value="all">All Projects</option>
                    {projectOptions.map((project) => (
                      <option key={project} value={project}>{project}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={2}>
                  <Button
                    variant="outline-secondary"
                    className="w-100"
                    onClick={() => {
                      setOwnerFilter('all');
                      setProjectFilter('all');
                    }}
                  >
                    Reset
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          <Row className="mb-4">
            <Col lg={6} className="mb-3">
              <Card className="shadow-sm h-100">
                <Card.Header className="bg-white border-bottom">
                  <h5 className="mb-0">Project-wise Exposure</h5>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={projectExposureBarData} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} height={80} />
                      <YAxis />
                      <Tooltip formatter={(value) => [formatCurrency(value), 'Exposure']} />
                      <Legend />
                      <Bar dataKey="value" name="Exposure" fill="#667eea" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6} className="mb-3">
              <Card className="shadow-sm h-100">
                <Card.Header className="bg-white border-bottom">
                  <h5 className="mb-0">Bank-wise Exposure</h5>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={320}>
                    <PieChart>
                      <Pie
                        data={bankExposureDonutData}
                        cx="50%"
                        cy="50%"
                        innerRadius={65}
                        outerRadius={105}
                        dataKey="value"
                        nameKey="name"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                      >
                        {bankExposureDonutData.map((entry, index) => (
                          <Cell key={`bank-cell-${entry.name}-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => [formatCurrency(value), 'Exposure']} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col lg={6} className="mb-3">
              <Card className="shadow-sm h-100">
                <Card.Header className="bg-white border-bottom">
                  <h5 className="mb-0">Expiry Timeline</h5>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={320}>
                    <LineChart data={expiryTimelineData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <Tooltip formatter={(value) => [`${value}`, 'Expiring BG']} />
                      <Legend />
                      <Line type="monotone" dataKey="value" name="Expiring BG Count" stroke="#e8590c" strokeWidth={2} />
                    </LineChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={6} className="mb-3">
              <Card className="shadow-sm h-100">
                <Card.Header className="bg-white border-bottom">
                  <h5 className="mb-0">Owner-wise Responsibility</h5>
                </Card.Header>
                <Card.Body>
                  <ResponsiveContainer width="100%" height={320}>
                    <BarChart data={ownerResponsibilityBarData} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" angle={-25} textAnchor="end" interval={0} height={80} />
                      <YAxis />
                      <Tooltip formatter={(value) => [formatCurrency(value), 'Responsibility']} />
                      <Legend />
                      <Bar dataKey="value" name="Amount" fill="#198754" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="shadow-sm mb-4">
            <Card.Header className="bg-white border-bottom d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Add Actional BG Entry</h5>
              <Button
                size="sm"
                variant={showAddForm ? 'outline-secondary' : 'primary'}
                onClick={() => {
                  setShowAddForm((prev) => !prev);
                  setAddFormError('');
                  setAddFormSuccess('');
                }}
              >
                {showAddForm ? 'Hide Form' : 'Add Entry'}
              </Button>
            </Card.Header>
            {showAddForm && (
              <Card.Body>
                {addFormError && <Alert variant="danger" className="mb-3">{addFormError}</Alert>}
                {addFormSuccess && <Alert variant="success" className="mb-3">{addFormSuccess}</Alert>}

                <Form onSubmit={handleCreateActionalBgDetail}>
                  <Row className="g-3">
                    <Col md={4}>
                      <Form.Label className="mb-1">Final Project</Form.Label>
                      <Form.Control value={addFormData.finalProject} onChange={(e) => handleAddFormChange('finalProject', e.target.value)} />
                    </Col>
                    <Col md={4}>
                      <Form.Label className="mb-1">Owner</Form.Label>
                      <Form.Control value={addFormData.owner} onChange={(e) => handleAddFormChange('owner', e.target.value)} />
                    </Col>
                    <Col md={4}>
                      <Form.Label className="mb-1">Purpose</Form.Label>
                      <Form.Control value={addFormData.purpose} onChange={(e) => handleAddFormChange('purpose', e.target.value)} />
                    </Col>

                    <Col md={4}>
                      <Form.Label className="mb-1">BU</Form.Label>
                      <Form.Control value={addFormData.bu} onChange={(e) => handleAddFormChange('bu', e.target.value)} />
                    </Col>
                    <Col md={4}>
                      <Form.Label className="mb-1">Project Name</Form.Label>
                      <Form.Control value={addFormData.projectName} onChange={(e) => handleAddFormChange('projectName', e.target.value)} />
                    </Col>
                    <Col md={4}>
                      <Form.Label className="mb-1">Beneficiary Name</Form.Label>
                      <Form.Control value={addFormData.beneficiaryName} onChange={(e) => handleAddFormChange('beneficiaryName', e.target.value)} />
                    </Col>

                    <Col md={4}>
                      <Form.Label className="mb-1">Reference No</Form.Label>
                      <Form.Control value={addFormData.referenceNo} onChange={(e) => handleAddFormChange('referenceNo', e.target.value)} />
                    </Col>
                    <Col md={4}>
                      <Form.Label className="mb-1">BG Type</Form.Label>
                      <Form.Control value={addFormData.bgType} onChange={(e) => handleAddFormChange('bgType', e.target.value)} />
                    </Col>
                    <Col md={4}>
                      <Form.Label className="mb-1">Bank Name</Form.Label>
                      <Form.Control value={addFormData.bankName} onChange={(e) => handleAddFormChange('bankName', e.target.value)} />
                    </Col>

                    <Col md={4}>
                      <Form.Label className="mb-1">BG No</Form.Label>
                      <Form.Control value={addFormData.bgNo} onChange={(e) => handleAddFormChange('bgNo', e.target.value)} />
                    </Col>
                    <Col md={4}>
                      <Form.Label className="mb-1">Amount (INR)</Form.Label>
                      <Form.Control type="number" step="0.01" value={addFormData.amountInr} onChange={(e) => handleAddFormChange('amountInr', e.target.value)} />
                    </Col>
                    <Col md={4}>
                      <Form.Label className="mb-1">Issue Date</Form.Label>
                      <Form.Control type="date" value={addFormData.issueDate} onChange={(e) => handleAddFormChange('issueDate', e.target.value)} />
                    </Col>

                    <Col md={4}>
                      <Form.Label className="mb-1">Valid Upto</Form.Label>
                      <Form.Control type="date" value={addFormData.validUpto} onChange={(e) => handleAddFormChange('validUpto', e.target.value)} />
                    </Col>
                    <Col md={4}>
                      <Form.Label className="mb-1">Claim Upto</Form.Label>
                      <Form.Control type="date" value={addFormData.claimUpto} onChange={(e) => handleAddFormChange('claimUpto', e.target.value)} />
                    </Col>
                    <Col md={4}>
                      <Form.Label className="mb-1">Target Timeline</Form.Label>
                      <Form.Control type="date" value={addFormData.targetTimeline} onChange={(e) => handleAddFormChange('targetTimeline', e.target.value)} />
                    </Col>

                    <Col md={12}>
                      <Form.Label className="mb-1">Remarks</Form.Label>
                      <Form.Control as="textarea" rows={2} value={addFormData.remarks} onChange={(e) => handleAddFormChange('remarks', e.target.value)} />
                    </Col>
                  </Row>

                  <div className="d-flex justify-content-end mt-3 gap-2">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      onClick={() => {
                        setAddFormData(INITIAL_ACTIONAL_FORM);
                        setAddFormError('');
                        setAddFormSuccess('');
                      }}
                      disabled={addFormSubmitting}
                    >
                      Reset
                    </Button>
                    <Button type="submit" variant="primary" disabled={addFormSubmitting}>
                      {addFormSubmitting ? 'Saving...' : 'Save to Table'}
                    </Button>
                  </div>
                </Form>
              </Card.Body>
            )}
          </Card>

          <Row>
            <Col>
              <Card className="shadow-sm">
                <Card.Header className="bg-white border-bottom">
                  <h5 className="mb-0">Actional BG Details</h5>
                </Card.Header>
                <Card.Body>
                  {actionalBgError && (
                    <Alert variant="warning" className="mb-3">
                      {actionalBgError}
                    </Alert>
                  )}

                  {activeRows.length === 0 ? (
                    <p className="text-muted mb-0">No Actional BG records found.</p>
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover size="sm" className="mb-0">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Final Project</th>
                            <th>Valid Upto</th>
                            <th>Validity</th>
                            <th>Risk Classification</th>
                            <th>Owner</th>
                            <th>Purpose</th>
                            <th>BU</th>
                            <th>Project Name</th>
                            <th>Beneficiary Name</th>
                            <th>Reference No</th>
                            <th>BG Type</th>
                            <th>Issue Date</th>
                            <th>Bank Name</th>
                            <th>BG No</th>
                            <th>Amount (INR)</th>
                            <th>Claim Upto</th>
                            <th>Remarks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedActiveRows.map((row, index) => {
                            const validityMeta = getValidityMeta(row);
                            const daysRemaining = getDaysUntilDate(row?.validUpto);
                            const validityCellClass = getActiveValidityCellClass(daysRemaining);
                            const risk = getRiskClassification(daysRemaining);
                            return (
                            <tr key={row.id || index} className={validityMeta.className}>
                              <td>{(activePageSafe - 1) * activePageSize + index + 1}</td>
                              <td>{row.finalProject || '-'}</td>
                              <td>{formatDate(row.validUpto)}</td>
                              <td className={`dashboard-validity-cell ${validityCellClass}`}>{validityMeta.label}</td>
                              <td><span className={`dashboard-risk-chip ${risk.className}`}>{risk.label}</span></td>
                              <td>{row.owner || '-'}</td>
                              <td>{row.purpose || '-'}</td>
                              <td>{row.bu || '-'}</td>
                              <td>{row.projectName || '-'}</td>
                              <td>{row.beneficiaryName || '-'}</td>
                              <td>{row.referenceNo || '-'}</td>
                              <td>{row.bgType || '-'}</td>
                              <td>{formatDate(row.issueDate)}</td>
                              <td>{row.bankName || '-'}</td>
                              <td>{row.bgNo || '-'}</td>
                              <td>{formatCurrency(row.amountInr)}</td>
                              <td>{formatDate(row.claimUpto)}</td>
                              <td>{row.remarks || '-'}</td>
                            </tr>
                          );
                        })}
                        </tbody>
                      </Table>
                      <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                          <small className="text-muted">
                            Showing {(activePageSafe - 1) * activePageSize + 1}-{Math.min(activePageSafe * activePageSize, activeRows.length)} of {activeRows.length}
                          </small>
                          <div className="d-flex align-items-center gap-2">
                            <small className="text-muted">Rows per page</small>
                            <Form.Select
                              size="sm"
                              style={{ width: 90 }}
                              value={activePageSize}
                              onChange={(e) => setActivePageSize(Number(e.target.value))}
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </Form.Select>
                          </div>
                        </div>
                        <Pagination size="sm" className="mb-0">
                          <Pagination.First onClick={() => setActivePage(1)} disabled={activePageSafe <= 1} />
                          <Pagination.Prev onClick={() => setActivePage((prev) => Math.max(1, prev - 1))} disabled={activePageSafe <= 1} />
                          <Pagination.Item active>{activePageSafe}</Pagination.Item>
                          <Pagination.Item disabled>{`/ ${activeTotalPages}`}</Pagination.Item>
                          <Pagination.Next onClick={() => setActivePage((prev) => Math.min(activeTotalPages, prev + 1))} disabled={activePageSafe >= activeTotalPages} />
                          <Pagination.Last onClick={() => setActivePage(activeTotalPages)} disabled={activePageSafe >= activeTotalPages} />
                        </Pagination>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col>
              <Card className="shadow-sm">
                <Card.Header className="bg-white border-bottom">
                  <h5 className="mb-0">Expired Actional BG Details</h5>
                </Card.Header>
                <Card.Body>
                  {expiredRows.length === 0 ? (
                    <p className="text-muted mb-0">No expired Actional BG records.</p>
                  ) : (
                    <div className="table-responsive">
                      <Table striped bordered hover size="sm" className="mb-0 dashboard-expired-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Final Project</th>
                            <th>Valid Upto</th>
                            <th>Validity</th>
                            <th>Owner</th>
                            <th>Purpose</th>
                            <th>BU</th>
                            <th>Project Name</th>
                            <th>Beneficiary Name</th>
                            <th>Reference No</th>
                            <th>BG Type</th>
                            <th>Issue Date</th>
                            <th>Bank Name</th>
                            <th>BG No</th>
                            <th>Amount (INR)</th>
                            <th>Claim Upto</th>
                            <th>Remarks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedExpiredRows.map((row, index) => {
                            const validityMeta = getValidityMeta(row);
                            return (
                              <tr key={`expired-${row.id || index}`} className={validityMeta.className}>
                                <td>{(expiredPageSafe - 1) * expiredPageSize + index + 1}</td>
                                <td>{row.finalProject || '-'}</td>
                                <td>{formatDate(row.validUpto)}</td>
                                <td className="dashboard-validity-cell">{validityMeta.label}</td>
                                <td>{row.owner || '-'}</td>
                                <td>{row.purpose || '-'}</td>
                                <td>{row.bu || '-'}</td>
                                <td>{row.projectName || '-'}</td>
                                <td>{row.beneficiaryName || '-'}</td>
                                <td>{row.referenceNo || '-'}</td>
                                <td>{row.bgType || '-'}</td>
                                <td>{formatDate(row.issueDate)}</td>
                                <td>{row.bankName || '-'}</td>
                                <td>{row.bgNo || '-'}</td>
                                <td>{formatCurrency(row.amountInr)}</td>
                                <td>{formatDate(row.claimUpto)}</td>
                                <td>{row.remarks || '-'}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                      <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                        <div className="d-flex align-items-center gap-3">
                          <small className="text-muted">
                            Showing {(expiredPageSafe - 1) * expiredPageSize + 1}-{Math.min(expiredPageSafe * expiredPageSize, expiredRows.length)} of {expiredRows.length}
                          </small>
                          <div className="d-flex align-items-center gap-2">
                            <small className="text-muted">Rows per page</small>
                            <Form.Select
                              size="sm"
                              style={{ width: 90 }}
                              value={expiredPageSize}
                              onChange={(e) => setExpiredPageSize(Number(e.target.value))}
                            >
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                              <option value={100}>100</option>
                            </Form.Select>
                          </div>
                        </div>
                        <Pagination size="sm" className="mb-0">
                          <Pagination.First onClick={() => setExpiredPage(1)} disabled={expiredPageSafe <= 1} />
                          <Pagination.Prev onClick={() => setExpiredPage((prev) => Math.max(1, prev - 1))} disabled={expiredPageSafe <= 1} />
                          <Pagination.Item active>{expiredPageSafe}</Pagination.Item>
                          <Pagination.Item disabled>{`/ ${expiredTotalPages}`}</Pagination.Item>
                          <Pagination.Next onClick={() => setExpiredPage((prev) => Math.min(expiredTotalPages, prev + 1))} disabled={expiredPageSafe >= expiredTotalPages} />
                          <Pagination.Last onClick={() => setExpiredPage(expiredTotalPages)} disabled={expiredPageSafe >= expiredTotalPages} />
                        </Pagination>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

const StatCard = ({ title, value, color, tooltip = '' }) => {
  const valueText = String(value ?? '');
  const valueSizeClass = valueText.length >= 14
    ? ' stat-value-xlong'
    : valueText.length >= 12
      ? ' stat-value-long'
      : '';
  const colorClasses = {
    primary: 'stat-primary',
    success: 'stat-success',
    info: 'stat-info',
    warning: 'stat-warning',
    danger: 'stat-danger',
    secondary: 'stat-secondary'
  };

  return (
    <Card className={`stat-card ${colorClasses[color]} shadow-sm`}>
      <Card.Body>
        <div>
          <div>
            <p className="stat-title d-flex align-items-center gap-1">
              {title}
              {tooltip ? (
                <OverlayTrigger placement="top" overlay={<BootstrapTooltip id={`finance-kpi-tip-${String(title).replace(/\s+/g, '-').toLowerCase()}`}>{tooltip}</BootstrapTooltip>}>
                  <span className="d-inline-flex align-items-center justify-content-center rounded-circle border text-secondary" style={{ width: 16, height: 16, lineHeight: 1 }}>
                    <FiInfo size={10} />
                  </span>
                </OverlayTrigger>
              ) : null}
            </p>
            <h2 className={`stat-value${valueSizeClass}`}>{value}</h2>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

export default FinanceDashboardPage;

